/**
 * Shared Cognito configuration utility
 * Consolidates Cognito config logic used across authentication modules
 */

import { CognitoUserPool } from "amazon-cognito-identity-js";

import logger from "../utils/logger";

/**
 * Interface for Cognito configuration
 */
export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  region: string;
}

/**
 * Environment-specific Cognito configurations
 * These are hardcoded in login-ui and detected at runtime based on URL
 * Note: Empty strings indicate Cognito is not yet configured for that environment
 */
const COGNITO_CONFIGS: Record<string, CognitoConfig> = {
  production: {
    userPoolId: "",
    clientId: "",
    region: "eu-west-1",
  },
  staging: {
    userPoolId: "eu-west-1_eaaoxNZK9",
    clientId: "49njjfaf764q1st8psr8rhm36t",
    region: "eu-west-1",
  },
};

/**
 * Get Cognito configuration based on runtime URL detection
 * Detects environment from window.location.host
 * Default: production config, unless URL contains "stage" or "localhost"
 *
 * @returns CognitoConfig object or null if Cognito is not configured
 */
export function getCognitoConfig(): CognitoConfig | null {
  if (typeof window === "undefined") {
    return null;
  }

  const useStaging =
    window.location.host.includes("stage") ||
    window.location.host.includes("localhost");

  const config = useStaging
    ? COGNITO_CONFIGS.staging
    : COGNITO_CONFIGS.production;

  // Return null if config values are empty (Cognito not configured for this env)
  if (!config.userPoolId || !config.clientId) {
    return null;
  }

  return config;
}

/**
 * Check if Cognito is configured and available
 * @returns true if Cognito is properly configured, false otherwise
 */
export function isCognitoConfigured(): boolean {
  const config = getCognitoConfig();
  return config !== null;
}

/**
 * Get Cognito User Pool instance
 * Creates a CognitoUserPool using the runtime configuration
 *
 * @returns CognitoUserPool instance or null if Cognito is not configured
 */
export function getUserPool(): CognitoUserPool | null {
  const config = getCognitoConfig();

  if (!config) {
    logger.warn("Cognito configuration not available for this environment", {
      environment: typeof window !== "undefined" ? window.location.host : "SSR",
    });
    return null;
  }

  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId,
  });
}
